import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import ArticleCard from '@/components/feature/ArticleCard';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, articles, videos, communityPosts } from '@/mocks/data';

const catMeta: Record<string, { title: string; desc: string; keywords: string }> = {
  shipin: { title: '挠脚心vk视频 – 热门挠脚心vk视频资源', desc: '浏览挠脚心vk精彩视频合集，发现最热门的挠痒挑战、趣味互动视频内容。', keywords: '挠脚心vk视频,vk挠痒视频,挠脚心视频资源,趣味视频' },
  shequ: { title: '挠脚心vk社区 – 活跃的挠脚心vk讨论社区', desc: '加入挠脚心vk社区，参与热门话题讨论，分享你的挠脚心vk体验和见解。', keywords: '挠脚心vk社区,挠脚心社区讨论,vk圈子,挠脚心交流' },
  wenzhang: { title: '挠脚心vk文章 – 深度挠脚心vk文章资讯', desc: '阅读挠脚心vk相关的深度文章与资讯，了解平台趋势、使用攻略和精彩内容。', keywords: '挠脚心vk文章,挠脚心资讯,挠脚心攻略,内容趋势' },
  huati: { title: '挠脚心vk话题 – 热门挠脚心vk话题标签', desc: '探索挠脚心vk热门话题与标签聚合，追踪最新最热的内容讨论方向。', keywords: '挠脚心vk话题,挠脚心标签,热门话题,话题讨论' },
  ziyuan: { title: '挠脚心vk资源 – 挠脚心vk资源合集导航', desc: '一站式挠脚心vk资源导航，涵盖视频、社区、工具等各类实用资源汇总。', keywords: '挠脚心vk资源,挠脚心资源合集,资源导航,内容汇总' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const meta = catMeta[slug || ''] || { title: '分类页面', desc: '', keywords: '' };

  const filteredArticles = articles.filter((a) => a.category === slug);
  const filteredVideos = slug === 'shipin' ? videos : [];
  const filteredPosts = slug === 'shequ' ? communityPosts : [];

  if (!category) {
    return (
      <PageLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEOMeta
        title={meta.title}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://naojiaoxinvk.com/category/${slug}`}
      />
      <SchemaOrg data={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: meta.title,
        description: meta.desc,
        url: `https://naojiaoxinvk.com/category/${slug}`,
        inLanguage: 'zh-CN',
      }} />
      <SchemaOrg data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naojiaoxinvk.com/' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://naojiaoxinvk.com/category/${slug}` },
        ],
      }} />

      <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />

      {/* Category Header */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <i className={`${category.icon} text-lg text-primary-600`}></i>
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">{category.name}</h1>
            <p className="text-sm text-foreground-500">{category.description}</p>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon}`}></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Videos Section */}
      {filteredVideos.length > 0 && (
        <section className="bg-background-100 py-8 md:py-10">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">挠脚心vk视频</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filteredVideos.map((v) => (
                <VideoCard key={v.slug} {...v} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community Posts */}
      {filteredPosts.length > 0 && (
        <section className="py-8 md:py-10">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">社区讨论帖</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPosts.map((post) => (
                <Link key={post.slug} to={`/article/naojiaoxin-vk-2024-redian-pan`} className="block group cursor-pointer">
                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 hover:border-primary-200 transition-colors">
                    <h3 className="text-sm font-medium text-foreground-900 mb-3 line-clamp-2 group-hover:text-primary-500 transition-colors">{post.title}</h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-user-line"></i>{post.author}</span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><i className="ri-chat-1-line"></i>{post.replies}</span>
                        <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{post.likes}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Articles */}
      {filteredArticles.length > 0 && (
        <section className={`py-8 md:py-10 ${filteredPosts.length > 0 ? 'bg-background-100' : ''}`}>
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Topics */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
        <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">热门标签</h2>
        <div className="flex flex-wrap gap-2">
          {(['naojiaoxin-vk-shipin', 'vk-naoyang-shipin', 'naojiaoxin-shequ', 'naojiaoxin-huati', 'naojiaoxin-ziyuan', 'vk-quanzi']).map((t) => (
            <Link key={t} to={`/article/naojiaoxin-vk-2024-redian-pan`} className="px-3 py-1.5 bg-secondary-100 text-secondary-900 rounded-full text-sm hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
              #{t.replace(/-/g, '')}
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}